import {AfterViewInit, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {
  EntityCrudPageTemplateComponent
} from '../../../layout/entity-crud-page-template/entity-crud-page-template.component';
import {DataTableColumnButton, TableColumn, TableLazyEvent} from '../../../shared/contracts/table-column.interface';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';
import {NotificationService} from '../../../shared/services/notification.service';
import {filter, finalize, switchMap, take} from 'rxjs';
import {DialogAction} from '../../../shared/contracts/dialog.interface';
import {ConfirmDialogComponent} from '../../../shared/components/dialog/confirm-dialog/confirm-dialog.component';
import {User} from '../../contracts/user.interface';
import {UserDialogComponent} from '../dialogs/user-dialog/user-dialog.component';
import {UserService} from '../../services/user.service';
import {SettingsService} from '../../../shared/services/settings_service';
import {Tag} from 'primeng/tag';
import {Message} from 'primeng/message';
import {USER_STATUS} from '../../../shared/enums/user-status.enum';
import {Tooltip} from 'primeng/tooltip';
import {AuthService} from '../../../auth/services/auth.service';

@Component({
  selector: 'app-users-list',
  imports: [
    EntityCrudPageTemplateComponent,
    Message,
    Tooltip
  ],
  providers: [UserService, DialogService, NotificationService],
  templateUrl: './users-list.component.html',
  styleUrl: './users-list.component.scss'
})
export class UsersListComponent implements OnInit, AfterViewInit {
  users: User[] = [];
  totalRecords = 0;
  rows = 10;
  loading = false;
  tenantSettings?: any;
  canCreate: boolean = true;
  onlineCount: number = 0;
  private lastLazyEvent: TableLazyEvent | undefined;

  @ViewChild('roleTemplate') roleTemplate!: TemplateRef<any>;
  @ViewChild('allowSelfAssignmentTemplate') allowSelfAssignmentTemplate!: TemplateRef<any>;
  @ViewChild('onlineTemplate') onlineTemplate!: TemplateRef<any>;

  columns: TableColumn[] = []

  _refDeleteUsersDialog: DynamicDialogRef | undefined;
  _refCreateUpdateUsersDialog: DynamicDialogRef | undefined;

  constructor(
    private userService: UserService,
    private notificationService: NotificationService,
    private dialogService: DialogService,
    private settingsService: SettingsService,
    private authService: AuthService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {}

  ngAfterViewInit() {
    this.prepareColumns();
    this.cd.detectChanges();
  }

  saveUser(): void {
    this._refCreateUpdateUsersDialog = this.dialogService.open(UserDialogComponent, {
      ...UserDialogComponent.DIALOG_CONFIG,
    });

    this._refCreateUpdateUsersDialog.onClose
      .pipe(
        filter((result) => result?.action === DialogAction.OK),
        switchMap(result =>  this.userService.create(result.data))
      ).subscribe((result) => {
      this.loadUsers(this.lastLazyEvent!);
      this.notificationService.success("Utente creato con successo.");
    });
  }

  editUser(user: User): void {
    this._refCreateUpdateUsersDialog = this.dialogService.open(UserDialogComponent, {
      ...UserDialogComponent.DIALOG_CONFIG,
      data: user,
    });

    this._refCreateUpdateUsersDialog.onClose
      .pipe(
        filter((result) => result?.action === DialogAction.OK),
        switchMap(result => this.userService.update(user.id!, result.data))
      ).subscribe(_ => {
      this.loadUsers(this.lastLazyEvent!);
      this.notificationService.success("Utente modificato con successo.");
    });
  }

  deleteUser(user: User): void {
    this._refDeleteUsersDialog = this.dialogService.open(ConfirmDialogComponent, {
      ...ConfirmDialogComponent.DIALOG_CONFIG,
      data: {message: `Sei sicuro di voler eliminare l'azienda ${user.username}`},
    });

    this._refDeleteUsersDialog.onClose
      .pipe(
        filter((result) => result?.action === DialogAction.OK),
        switchMap(_ => this.userService.delete(user.id!)),
      ).subscribe((_) => {
      this.loadUsers(this.lastLazyEvent!);
      this.notificationService.success("Utente eliminato con successo.");
    });
  }

  deleteSelectedUsers(users: User[]): void {
    this.openDeleteUsersDialog(users)
  }

  private openDeleteUsersDialog(users: User[]): void {
    this._refDeleteUsersDialog = this.dialogService.open(ConfirmDialogComponent, {
      ...ConfirmDialogComponent.DIALOG_CONFIG,
      data: {message: `Sei sicuro di voler eliminare ${users.length} utenti?`,}
    });

    this._refDeleteUsersDialog.onClose
      .pipe(
        filter((result) => result?.action === DialogAction.OK),
      ).subscribe((_) => {});
  }

  loadUsers(event: TableLazyEvent): void {
    this.lastLazyEvent = event;
    this.loading = true;
    this.userService.getAll(event.page, event.rows, event.filters).pipe(
      take(1),
      finalize(() => this.loading = false)
    ).subscribe(data => {
      this.users = data.data;
      this.totalRecords = data.total;
      this.onlineCount = this.users.filter(user => user.status === USER_STATUS.ONLINE).length;
      this.getSettingsAndValidateCreationAction();
    });
  }

  private getSettingsAndValidateCreationAction(): void {
    this.tenantSettings = this.settingsService.getTenantSettings();

    if (!this.tenantSettings) {
      this.canCreate = true;
      return;
    }

    if (this.tenantSettings.features?.users?.limit !== undefined &&
      this.totalRecords >= this.tenantSettings.features.users.limit) {
      this.canCreate = false;
    } else {
      this.canCreate = true;
    }
  }

  private prepareColumns() {
    this.columns = [
      { field: 'isOnline', header: 'Online', sortable: false, customTemplate: this.onlineTemplate, type: 'template'},
      { field: 'firstName', header: 'Nome', sortable: false, filterable: true  },
      { field: 'lastName', header: 'Cognome', sortable: false, filterable: true  },
      { field: 'email', header: 'Email', sortable: false },
      { field: 'username', header: 'Username', sortable: false },
      { field: 'roleId', header: 'Supervisore', sortable: false, customTemplate: this.roleTemplate, type: 'template'},
      { field: 'allowSelfAssignment', header: 'Autoassegnazione', sortable: false, customTemplate: this.allowSelfAssignmentTemplate, type: 'template'},
      { header: 'Azioni', buttons: <DataTableColumnButton[]>[
          {icon: 'pi-pencil', click: this.editUser.bind(this), tooltip: 'Modifica', color: 'primary' },
          {icon: 'pi-trash', click: this.deleteUser.bind(this), tooltip: 'Elimina', color: 'danger'},
        ]}
    ];
  }

  protected readonly USER_STATUS = USER_STATUS;
}
